import React, { useEffect, useState } from 'react';

import {
  Container,
  Icon,
  InputWrapper,
  Label,
  PasswordInput,
  Required,
  TextAlign,
  ToggleButton,
  ForgotPasswordLink,
  ForgotPasswordWrapper,
} from './styles';

interface CustomInputPasswordProps {
  name: string;
  title?: string;
  value?: string;
  disabled?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  required?: boolean;
  textAlign?: TextAlign;
  placeholder?: string;
  maxLength?: number;

  showToggle?: boolean;

  showLoginButton?: boolean;
  onLogin?: () => void;

  showForgotPassword?: boolean;
  onForgotPassword?: () => void;

  id?: string;
  'data-testid'?: boolean | string;
}

const CustomInputPassword: React.FC<CustomInputPasswordProps> = ({
  name,
  title,
  value = '',
  disabled = false,
  onChange,
  required = false,
  textAlign = 'left',
  placeholder,
  maxLength,
  showToggle = true,

  showLoginButton = false,
  onLogin,

  showForgotPassword = false,
  onForgotPassword,

  id,
  'data-testid': dataTestId,
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInternalValue(newValue);
    onChange?.(event);
  };

  const controlId = id || name;
  const type = showPassword ? 'text' : 'password';

  const testId =
    typeof dataTestId === 'string'
      ? dataTestId
      : dataTestId === true
        ? `input-password-${controlId}`
        : undefined;

  const handleLogin = () => {
    if (disabled) return;
    onLogin?.();
  };

  const handleForgot = () => {
    if (disabled) return;
    onForgotPassword?.();
  };

  return (
    <Container controlId={controlId}>
      {title && (
        <Label data-testid={testId ? `${testId}-label` : undefined}>
          {title}
          {required && <Required>*</Required>}
        </Label>
      )}

      <InputWrapper disabled={disabled}>
        <PasswordInput
          type={type}
          name={name}
          id={controlId}
          value={internalValue}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          maxLength={maxLength}
          textAlign={textAlign}
          hasToggle={showToggle || showLoginButton}
          data-testid={testId}
          autoComplete="current-password"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault(); // evita comportamentos estranhos
              onLogin?.(); // dispara login
            }
          }}
        />

        {/* 👁 Toggle mostrar/ocultar */}
        {showToggle && (
          <ToggleButton
            type="button"
            variant="toggle"
            onClick={() => setShowPassword((prev) => !prev)}
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            active={showPassword}
            disabled={disabled}
            data-testid={testId ? `${testId}-toggle` : undefined}
          >
            <Icon
              className="material-icons"
              data-testid={testId ? `${testId}-icon` : undefined}
            >
              {showPassword ? 'visibility_off' : 'visibility'}
            </Icon>
          </ToggleButton>
        )}

        {/* ➜ Botão de Login (seta para a direita) */}
        {showLoginButton && onLogin && (
          <ToggleButton
            type="button"
            variant="login"
            onClick={handleLogin}
            aria-label="Entrar"
            disabled={disabled}
            active={false}
            data-testid={testId ? `${testId}-login` : undefined}
          >
            <Icon className="material-icons">arrow_forward</Icon>
          </ToggleButton>
        )}
      </InputWrapper>

      {/* "Esqueci a senha" alinhado à direita */}
      {showForgotPassword && onForgotPassword && (
        <ForgotPasswordWrapper>
          <ForgotPasswordLink
            type="button"
            onClick={handleForgot}
            disabled={disabled}
            data-testid={testId ? `${testId}-forgot` : undefined}
          >
            Esqueci a senha
          </ForgotPasswordLink>
        </ForgotPasswordWrapper>
      )}
    </Container>
  );
};

export default CustomInputPassword;
